import { yupResolver } from '@hookform/resolvers/yup';
import Paper from '@mui/material/Paper';
import React, { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { generatePath, useSearchParams } from 'react-router';

import {
  ContactCreationRequestDto,
  ContactDto,
  ContactPropertiesDto,
  CustomerPropertiesDto,
  ResourceTypeDto,
} from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { ViewWrapperNew } from '@/components/viewWrapperNew/ViewWrapperNew.comp.tsx';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { ContactCreateEditForm } from '@/views/contactsCreate/components/ContactCreateEditForm.comp.tsx';
import { ContactResourceDetails } from '@/views/contactsCreate/components/ContactResourceDetails.comp.tsx';
import { CreateContactFormModel } from '@/views/contactsCreate/model/CreateContactForm.model.ts';
import { mapToContactCreationRequestDto } from '@/views/contactsCreate/util/mapToContactCreationRequestDto.util.ts';
import { useCreateContact } from '@/views/contactsCreate/util/useCreateContact.util.ts';
import { useCreateContactFormValidation } from '@/views/contactsCreate/util/useCreateContactForm.validation.ts';
import { useUpdateContact } from '@/views/contactsCreate/util/useUpdateContact.util.ts';
import { useUpdateContactCustomer } from '@/views/contactsCreate/util/useUpdateContactCustomer.util.ts';

type Props = {
  contactDetails?: ContactDto;
  initialCustomer?: CustomerPropertiesDto;
  onCancel?: () => void;
  onPostCreateAction?: (customer: ContactPropertiesDto) => void;
};

const ContactsCreateView: React.FC<Props> = ({
  contactDetails,
  initialCustomer,
  onCancel,
  onPostCreateAction,
}) => {
  const validation = useCreateContactFormValidation();
  const { createContact, isCreatingContact } = useCreateContact();
  const { isUpdatingContact, updateContact } = useUpdateContact();
  const { push } = useAppNavigation();
  const [params] = useSearchParams();
  const { contactStatusOptions } = useGetSelectOptions();
  const resourceId = params.get('resourceId');
  const resourceType = params.get('resourceType');
  const { USERS_SHORT } = useDictionariesContext();
  const { setContactCustomer, updateContactCustomer } = useUpdateContactCustomer();
  const { userSelectOption } = useUserContext();

  const contactOwnerId: SelectSearchOptionModel | null = useMemo(() => {
    if (!contactDetails) {
      return userSelectOption;
    }
    return USERS_SHORT.find(it => it.value === contactDetails.basicContact.contactOwnerId) ?? null;
  }, [USERS_SHORT, contactDetails, userSelectOption]);

  const formMethods = useForm<CreateContactFormModel>({
    defaultValues: contactDetails
      ? {
          contact: {
            ...contactDetails.basicContact,
            contactOwnerId,
            customer: initialCustomer,
            customerChanged: false,
            name: contactDetails.basicContact.displayName,
            status: contactStatusOptions.find(
              it => it.value === contactDetails.basicContact.status,
            ),
          },
        }
      : {
          contact: {
            contactOwnerId,
            customer: null,
            customerChanged: false,
            status: contactStatusOptions[0],
          },
        },
    resolver: yupResolver(validation),
  });

  const handleSubmit: SubmitHandler<CreateContactFormModel> = async data => {
    const { contact } = data;
    const { customer } = contact;
    const dto: ContactCreationRequestDto = mapToContactCreationRequestDto(contact);

    if (resourceType && resourceId) {
      dto.resourceId = {
        resourceId: resourceId,
        type: resourceType as ResourceTypeDto,
      };
    }

    if (!contactDetails) {
      const res = await createContact({ dto });
      await setContactCustomer({ contactId: res.contactId, customer });

      if (onPostCreateAction) {
        onPostCreateAction(dto);
        return;
      }

      if (resourceId) {
        push(generatePath(RouteEnum.CUSTOMERS_DETAILS, { customerId: resourceId }));
      } else {
        push(generatePath(RouteEnum.CONTACTS_DETAILS, { contactId: res.contactId.toString() }));
      }
    } else {
      const { contactId } = contactDetails.basicContact;
      await updateContact({
        contactId,
        dto,
      });

      await updateContactCustomer({
        contactId,
        customer,
        customerChanged: contact.customerChanged ?? false,
        initialCustomerId: initialCustomer?.customerId,
      });

      push(generatePath(RouteEnum.CONTACTS_DETAILS, { contactId: contactId.toString() }));
    }
  };

  const disabled = isCreatingContact || isUpdatingContact;
  return (
    <ViewWrapperNew withSpacing>
      {resourceId && resourceType && (
        <Paper sx={{ p: 2 }}>
          <ContactResourceDetails resourceId={Number(resourceId)} resourceType={resourceType} />
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        <ContactCreateEditForm
          disabled={disabled}
          formMethods={formMethods}
          hideCustomerSelection={!!resourceType}
          isEditing={!!contactDetails}
          onCancel={onCancel}
          onSubmit={handleSubmit}
          validation={validation}
        />
      </Paper>
    </ViewWrapperNew>
  );
};

export default ContactsCreateView;
